import { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'

import { useTranslation } from 'react-i18next'
import { Country, PyramidType, PyramidValueDto } from '../../../types'
import usePyramidValuesAndReferencePyramids from '../../../graphql/pyramids/hooks/usePyramidValuesAndReferencePyramids'
import { useEmployeeLevelsByCountry } from '../../../graphql/employee/hooks/useEmployeeLevelsByCountry'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { formatPyramidReference, formatPyramidValues, formatPyramidValuesForDashboard } from '../../pyramids/pyramidUtils'
import { useDashboardStore } from '../DashboardStore'
import { DashboardPyramidsGraph } from './DashboardPyramidsGraph'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
export const DashboardPyramidContent: FC<{
  type: PyramidType
}> = ({ type }) => {
  const [selected, selectedCountry, selectedEmployeeLevels, setAppraisalMonitoringEmployeeLevel] = useDashboardStore(
    (state) => [
      state.selected,
      state.selectedCountry,
      state.appraisalMonitoringFilters.selectedEmployeeLevels,
      state.setAppraisalMonitoringEmployeeLevel,
    ],
    shallow,
  )
  const companyId = selected.value
  const country = selectedCountry.value as Country
  const { t } = useTranslation()

  const { data, loading, error } = usePyramidValuesAndReferencePyramids(companyId, country, type)

  const { data: employeeLevelsData, error: employeeLevelsError, loading: employeeLevelsLoading } = useEmployeeLevelsByCountry(country)

  useEffect(() => {
    if (!error && !employeeLevelsError) {
      return
    }
    showNotification({
      type: 'error',
      message: t('dashboard.companyManager.filtersArea.pyramids.error.title'),
      description: t('dashboard.companyManager.filtersArea.pyramids.error.description'),
    })
  }, [error, employeeLevelsError, t])

  const pyramidValues: PyramidValueDto[] | undefined =
    !loading && !employeeLevelsLoading
      ? formatPyramidValues(data!.pyramidValues, country, type, employeeLevelsData!.employeeLevels)
      : undefined

  const recommendedValues =
    !loading && !employeeLevelsLoading
      ? data!.referencePyramid && data!.referencePyramid.referencePyramidDetails
        ? formatPyramidReference(data!.referencePyramid.referencePyramidDetails, country, type, employeeLevelsData!.employeeLevels)
        : undefined
      : undefined

  const recommendedData = recommendedValues ? recommendedValues.sort((x, y) => x.displayOrder - y.displayOrder) : []

  const pyramidsData = pyramidValues ? formatPyramidValuesForDashboard(pyramidValues, recommendedData) : undefined
  const isLoading = loading || employeeLevelsLoading

  return (
    <>
      {isLoading || !!error || !!employeeLevelsError ? (
        <PerFlexRow justify="center" align="middle" className="fullHeight">
          <NewLoadingAndErrors
            t={t}
            isLoading={isLoading}
            hasNoData={false}
            showSpinner
            error={error || employeeLevelsError}
            iconSize={100}
            showOnlyIcon
          />
        </PerFlexRow>
      ) : (
        <DashboardPyramidsGraph
          data={pyramidsData!}
          selectedLevelPill={selectedEmployeeLevels}
          onLevelLabelClick={(levelPill: string) => setAppraisalMonitoringEmployeeLevel(levelPill)}
        />
      )}
    </>
  )
}
